// src/composables/useTaskSSE.js
import { ref, onMounted, onUnmounted } from 'vue';
import API from '@/services/address';
import { useUser } from '@/composables/useUser';
import CryptoJS from 'crypto-js';

// 加密配置（与 axiosInstance 保持一致）
const AES_KEY = window.APP_CONFIG?.AES_KEY || process.env.VUE_APP_AES_KEY || 'bingo@408_AES_KEY_TEST';

/**
 * 任务 SSE 通知管理的 Composable
 * 使用 Fetch API + ReadableStream 实现，支持自定义请求头和签名
 * 
 * @param {Object} options - 配置选项
 * @param {Function} options.onTaskCompleted - 任务完成回调
 * @param {Function} options.onProgressUpdate - 进度更新回调
 * @param {boolean} options.autoConnect - 是否自动连接（默认 true）
 * @returns {Object} SSE 状态和方法
 */
export function useTaskSSE(options = {}) {
  const {
    onTaskCompleted = null,
    onProgressUpdate = null,
    autoConnect = true
  } = options;

  // 响应式状态
  const isConnected = ref(false);
  const lastNotification = ref(null);

  // 内部状态
  let abortController = null;
  let reconnectTimer = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  // 获取用户信息
  const { userProfile, isLoggedIn } = useUser();

  /**
   * 生成签名（与 axiosInstance 保持一致）
   */
  const generateSignature = (params) => {
    const dataToEncrypt = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');
    return CryptoJS.HmacSHA256(
      JSON.stringify(dataToEncrypt),
      CryptoJS.enc.Utf8.parse(AES_KEY)
    ).toString();
  };

  /**
   * 解析 SSE 数据
   */
  const parseSSEData = (text) => {
    const events = [];
    const lines = text.split('\n');
    let currentEvent = { event: 'message', data: '' };

    for (const line of lines) {
      if (line.startsWith('event:')) {
        currentEvent.event = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        currentEvent.data += line.substring(5).trim();
      } else if (line === '') {
        // 空行表示事件结束
        if (currentEvent.data) {
          events.push({ ...currentEvent });
        }
        currentEvent = { event: 'message', data: '' };
      }
    }

    return events;
  };

  /**
   * 处理 SSE 事件
   */
  const handleSSEEvent = (event) => {
    console.log('[SSE] 收到事件:', event.event, event.data);

    if (event.event === 'heartbeat') {
      console.debug('[SSE] 收到心跳');
      return;
    }

    if (event.event === 'task-notification') {
      try {
        const notification = JSON.parse(event.data);
        console.log('[SSE] 收到任务通知:', notification);
        
        lastNotification.value = notification;

        if (notification.type === 'TASK_COMPLETED' && onTaskCompleted) {
          onTaskCompleted(notification);
        } else if (notification.type === 'PROGRESS_UPDATE' && onProgressUpdate) {
          onProgressUpdate(notification);
        }
      } catch (error) {
        console.error('[SSE] 解析通知数据失败:', error);
      }
    }
  };

  /**
   * 建立 SSE 连接（使用 Fetch API）
   */
  const connect = async () => {
    console.log("[SSE] 开始建立连接");
    
    // 检查用户是否已登录
    if (!isLoggedIn.value || !userProfile.value?.userId) {
      console.warn('[SSE] 用户未登录，无法建立 SSE 连接');
      return;
    }

    // 如果已有连接，先断开
    if (abortController) {
      disconnect();
    }

    const userId = userProfile.value.userId;
    const token = localStorage.getItem('authToken');
    const timestamp = new Date().getTime();
    
    // 构建请求参数
    const params = { userId };
    const signature = generateSignature(params);
    
    // 构建 URL
    const url = `${API.sseConnect}?userId=${userId}`;
    
    console.log('[SSE] 正在建立连接...', url);

    // 创建 AbortController 用于取消请求
    abortController = new AbortController();

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'AuthToken': `Bearer ${token}`,
          'timestamp': timestamp.toString(),
          'signature': signature
        },
        signal: abortController.signal,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('[SSE] 连接已建立');
      isConnected.value = true;
      reconnectAttempts = 0;

      // 读取流数据
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isStreamActive = true;

      while (isStreamActive) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('[SSE] 流已结束，准备重连...');
          isStreamActive = false;
          isConnected.value = false;
          // 流结束时触发重连
          handleReconnect();
          break;
        }

        // 解码数据
        buffer += decoder.decode(value, { stream: true });
        
        // 按双换行符分割事件
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || ''; // 保留不完整的部分

        // 处理完整的事件
        for (const part of parts) {
          if (part.trim()) {
            const events = parseSSEData(part + '\n\n');
            events.forEach(handleSSEEvent);
          }
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[SSE] 连接已被取消');
      } else {
        console.error('[SSE] 连接错误:', error);
        isConnected.value = false;
        handleReconnect();
      }
    }
  };

  /**
   * 处理断线重连
   */
  const handleReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error('[SSE] 重连次数已达上限，停止重连');
      return;
    }

    reconnectAttempts++;
    console.log(`[SSE] 将在 ${reconnectDelay / 1000} 秒后尝试第 ${reconnectAttempts} 次重连...`);

    reconnectTimer = setTimeout(() => {
      connect();
    }, reconnectDelay);
  };

  /**
   * 断开 SSE 连接
   */
  const disconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (abortController) {
      abortController.abort();
      abortController = null;
      console.log('[SSE] 连接已断开');
    }

    isConnected.value = false;
    reconnectAttempts = 0;
  };

  // 组件挂载时自动连接
  onMounted(() => {
    if (autoConnect) {
      setTimeout(() => {
        if (isLoggedIn.value) {
          connect();
        }
      }, 500);
    }
  });

  // 组件卸载时断开连接
  onUnmounted(() => {
    disconnect();
  });

  return {
    isConnected,
    lastNotification,
    connect,
    disconnect
  };
}

export default useTaskSSE;
